import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { BlockNoteEditorComponent } from '@/components/atoms/Editors/BlockNoteEditor/BlockNoteEditor';
import { getGuidelinesEditorOverrides } from '@/components/atoms/Editors/BlockNoteEditor/config';
import { Skeleton } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import type { Dialog } from '@/shared/types/dialog';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { GuidelineEditorHeader } from './GuidelineEditorHeader';
import { getMergedInstructions } from '../../helper';

type CustomInstructionsEditorProps = {
  onEditorReady?: (editor: { getMarkdown: () => Promise<string> }) => void;
  onChange?: () => void;
  enabledTools?: string[];
  dialogs?: Dialog[];
  onNavigateToLocalTools?: () => void;
};

export const CustomInstructionsEditor = ({
  onEditorReady,
  onChange,
  enabledTools,
  dialogs,
  onNavigateToLocalTools,
}: CustomInstructionsEditorProps = {}) => {
  const { t } = useTranslation();

  const brainId = useSelector(selectBrainId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { brain, patchBrainGuidelines } = useBrains(brainId);
  const { contextVariables } = useBrainVariables();

  const shouldAppendCustomGuidelinesWithInstructions = useMemo(
    () =>
      Array.isArray(brain?.guidelines?.custom_guidelines) &&
      brain?.guidelines?.custom_guidelines.length > 0,
    [brain?.guidelines?.custom_guidelines]
  );

  const location = useLocation();
  const navigate = useNavigate();

  const editorApiRef = useRef<{
    appendContent: (markdown: string) => void;
    getMarkdown: () => Promise<string>;
  } | null>(null);

  const isMergingRef = useRef(false);

  const saveCustomInstructions = useCallback(
    (markdownToSave: string) => {
      if (!canWrite || !brainId || isMergingRef.current) {
        return;
      }

      isMergingRef.current = true;

      // Save merged instructions and clear custom_guidelines
      patchBrainGuidelines({
        brain_id: brainId,
        custom_instructions: markdownToSave,
        custom_guidelines: [],
        preventToast: true, // Silent merge, no toast notification
      });
    },
    [canWrite, brainId, patchBrainGuidelines]
  );

  const handleEditorSave = useCallback(
    (_markdown: string) => {
      if (!canWrite) {
        return;
      }
    },
    [canWrite]
  );

  // Append custom instructions with custom guidelines
  // This runs when custom_guidelines exist, preventing race condition with editor init
  useEffect(() => {
    if (!canWrite) {
      return;
    }

    if (shouldAppendCustomGuidelinesWithInstructions && !isMergingRef.current) {
      const mergedInstructions = getMergedInstructions(
        brain?.guidelines?.custom_instructions,
        brain?.guidelines?.custom_guidelines
      );

      saveCustomInstructions(mergedInstructions);
    }

    // Reset merge flag when custom_guidelines becomes empty (merge completed)
    if (!shouldAppendCustomGuidelinesWithInstructions) {
      isMergingRef.current = false;
    }
  }, [
    shouldAppendCustomGuidelinesWithInstructions,
    canWrite,
    saveCustomInstructions,
    brain?.guidelines?.custom_instructions,
    brain?.guidelines?.custom_guidelines,
  ]);

  const handleEditorReady = useCallback(
    (api: { appendContent: (markdown: string) => void; getMarkdown: () => Promise<string> }) => {
      editorApiRef.current = api;
      onEditorReady?.(api);
    },
    [onEditorReady]
  );

  useEffect(() => {
    if (location.hash === '#create-new-guideline' && editorApiRef.current) {
      const newGuidelineText = `\n\n# ${t('ai_agents.guidelines_editor_overrides.heading2.title')}\n\n`;
      editorApiRef.current.appendContent(newGuidelineText);

      // Clean the hash from the URL after handling it
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location, navigate, t]);

  // Show skeleton while merging custom_guidelines into custom_instructions
  // Once merge completes and custom_guidelines becomes empty, editor will render
  if (shouldAppendCustomGuidelinesWithInstructions) {
    return (
      <>
        <GuidelineEditorHeader title={t('ai_agents.knowledge.custom_instructions.title')} />
        <div className="space-y-4 p-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </div>
      </>
    );
  }

  // Render editor only after merge completes (custom_guidelines is empty)
  return (
    <>
      <GuidelineEditorHeader title={t('ai_agents.knowledge.custom_instructions.title')} />

      <BlockNoteEditorComponent
        onSave={handleEditorSave}
        initialMarkdown={brain?.guidelines?.custom_instructions}
        dictionary={getGuidelinesEditorOverrides().dictionary}
        onEditorReady={handleEditorReady}
        editable={canWrite}
        onChange={onChange}
        enabledTools={enabledTools}
        dialogs={dialogs}
        onNavigateToLocalTools={onNavigateToLocalTools}
        contextVariableOptions={contextVariables}
      />
    </>
  );
};
